import {
  ref,
  useAuth,
  useDialog,
  type useMetrics,
  useRoute,
  watch,
} from "#imports";
import { useIdle } from "@vueuse/core";
import type { ShallowRef } from "vue";
import MPJoinMessage from "~/components/dialogs/MPJoinMessage.vue";
import type mapboxgl from "mapbox-gl";

type NagScreens = {
  map: ShallowRef<mapboxgl.Map | undefined, mapboxgl.Map | undefined>;
  // nuxt doesn't cope with not doing this in the setup(), so we push it in after it's declared there
  metrics: ReturnType<typeof useMetrics>;
};

export const useNagScreens = ({ map, metrics: { trackEvent } }: NagScreens) => {
  const { openDialog } = useDialog();
  const authStore = useAuth();
  const route = useRoute();

  // nag screens
  const openAuthScreen = async () => {
    openDialog(MPJoinMessage, {}, { blockClose: true });
  };

  const viewedSpotCount = ref<number>(0);
  watch(route, () => {
    if (authStore.isLoggedIn) return;
    if (route.path === "/explore") return;

    if (++viewedSpotCount.value > 2) {
      trackEvent("joined", {
        friction: "join_prompt_on_third_spot_click",
      });
      openAuthScreen();
    }
  });

  map.value!.on("zoom", () => {
    if (authStore.isLoggedIn) return;
    const zoom = map.value!.getZoom()!;

    if (zoom == 4 || zoom == 6 || zoom == 10) {
      trackEvent("joined", {
        friction: "join_prompt_on_map_zoom",
      });
      openAuthScreen();
    }
  });

  const dragEvents = ref<number>(0);
  map.value!.on("moveend", () => {
    if (authStore.isLoggedIn) return;

    if (dragEvents.value++ > 1) {
      trackEvent("joined", {
        friction: "join_prompt_on_map_drag",
      });
      openAuthScreen();
    }
  });

  const { idle, reset } = useIdle(5 * 60 * 1000); // every 5 minutes

  watch(idle, () => {
    if (authStore.isLoggedIn) return;
    if (idle.value) {
      openAuthScreen();
      reset();
    }
  });
};
